import { Helmet } from 'react-helmet-async';

/**
 * SEO Component - Provides meta tags for each page
 * 
 * Used for:
 * - Page titles
 * - Meta descriptions
 * - Open Graph (Facebook, LinkedIn)
 * - Twitter cards
 * - Canonical URLs
 * - JSON-LD structured data
 * 
 * Note: The HTML lang attribute is managed by LanguageContext
 * for immediate synchronization. This component handles meta tags.
 */
export function SEO({
  title,
  description,
  canonical,
  ogImage,
  ogType = 'website',
  twitterCard = 'summary_large_image',
  jsonLd,
  noIndex = false,
  lang = 'en',
}) {
  const siteUrl = 'https://pecashglobal.com';
  const defaultTitle = 'PeCash - Cross-Border Family Purchases';
  const defaultDescription = 'PeCash enables U.S. customers to authorize household purchases for family members at participating Dominican merchants.';
  const defaultImage = `${siteUrl}/og-image.png`;

  const pageTitle = title ? `${title} | PeCash` : defaultTitle;
  const pageDescription = description || defaultDescription;
  const pageImage = ogImage || defaultImage;
  const pageUrl = canonical || siteUrl;

  return (
    <Helmet htmlAttributes={{ lang }}>
      {/* Basic Meta */}
      <title>{pageTitle}</title>
      <meta name="description" content={pageDescription} />
      <link rel="canonical" href={pageUrl} />
      
      {/* No Index for private pages */}
      {noIndex && <meta name="robots" content="noindex, nofollow" />}
      
      {/* Open Graph */}
      <meta property="og:type" content={ogType} />
      <meta property="og:title" content={pageTitle} />
      <meta property="og:description" content={pageDescription} />
      <meta property="og:image" content={pageImage} />
      <meta property="og:url" content={pageUrl} />
      <meta property="og:site_name" content="PeCash" />
      <meta property="og:locale" content={lang === 'es' ? 'es_DO' : 'en_US'} />
      <meta property="og:locale:alternate" content={lang === 'es' ? 'en_US' : 'es_DO'} />
      
      {/* Twitter Card */}
      <meta name="twitter:card" content={twitterCard} />
      <meta name="twitter:title" content={pageTitle} />
      <meta name="twitter:description" content={pageDescription} />
      <meta name="twitter:image" content={pageImage} />
      
      {/* Additional Meta */}
      <meta name="application-name" content="PeCash" />
      <meta name="theme-color" content="#10b981" />
      
      {/* JSON-LD Structured Data */}
      {jsonLd && (
        <script type="application/ld+json">
          {JSON.stringify(jsonLd)}
        </script>
      )}
    </Helmet>
  );
}

/**
 * Pre-built JSON-LD schemas for common page types
 */
export const jsonLdSchemas = {
  organization: {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: 'PeCashGlobal',
    url: 'https://pecashglobal.com',
    logo: 'https://pecashglobal.com/logo.png',
    description: 'Cross-border family commerce platform connecting U.S. customers with Dominican merchants.',
    sameAs: [
      // Add social media URLs when available
    ],
    contactPoint: {
      '@type': 'ContactPoint',
      email: 'support@pecashglobal.com',
      contactType: 'customer support',
      availableLanguage: ['English', 'Spanish'],
    },
  },
  
  financeService: {
    '@context': 'https://schema.org',
    '@type': 'FinancialService',
    name: 'PeCash',
    description: 'Enable family members to make purchases at Dominican merchants',
    url: 'https://pecashglobal.com',
    areaServed: [
      {
        '@type': 'Country',
        name: 'United States',
      },
      {
        '@type': 'Country',
        name: 'Dominican Republic',
      },
    ],
    serviceType: 'Cross-border purchase authorization',
  },
  
  faqPage: (faqs) => ({
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map(faq => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer,
      },
    })),
  }),
  
  breadcrumb: (items) => ({
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.name,
      item: item.url,
    })),
  }),
};

export default SEO;
